import { Icon } from 'semantic-ui-react'

import { useAppSelector, useActionless, useActions } from 'redux/hooks'
import { SET_ACTIVE_STATE } from 'redux/active/actions/types'
import { modifyCollectionCardMapAction } from 'redux/collectionV2/actions'

import PinService from 'services/pin.service'
import ToastService from 'services/toast.service'

import { CollectionCardWrapperChildProps } from 'components/collectionV2/views/CardWrapper'

import { PRICE_ICON_NAMES, PRICE_NAMES, PRICE_SOURCE } from 'types/active'
import { OWNED } from 'types/deck'

import BasicCard from 'components/card/BasicCard'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import CornerQuantity from 'components/collectionV2/views/Image/ImageCollectionCard/CornerQuantity'
import { MultiSelected } from 'components/card/controlsAndInfo/cardOverlays'

import { getPriceStringAndUrl } from 'utils/Price'

import styles from './imageCollectionCard.module.scss'

const ImageCollectionCard = ({
  collectionCard,
  actions,
  extraData,
  localCardData,
}: CollectionCardWrapperChildProps) => {
  const [setActiveState] = useActionless(SET_ACTIVE_STATE)
  const [modifyCollectionCard] = useActions(modifyCollectionCardMapAction)

  const imageSize = useAppSelector(state => state.collectionV2.imageSize)

  const priceSource = useAppSelector(state => state.collectionV2.priceSource)
  const priceSource2 = useAppSelector(state => state.collectionV2.priceSource2)
  const ownsCollection = useAppSelector(state => state.collectionV2.ownsCollection)

  const price = getPriceStringAndUrl(collectionCard.card, priceSource, collectionCard.modifier !== 'Normal')
  const price2 = getPriceStringAndUrl(collectionCard.card, priceSource2, collectionCard.modifier !== 'Normal')

  const priceName = PRICE_NAMES[priceSource]
  const priceName2 = PRICE_NAMES[priceSource2]

  const handlePinCard = () => {
    const card = collectionCard.card
    if (card.pinnedStatus !== 0)
      return setActiveState({ pinnedCardsOverlay: true, pinnedCardsFilteredCards: [card.name] })

    PinService.add(card)
      .then(() => {
        modifyCollectionCard({ ...collectionCard, card: { ...card, pinnedStatus: OWNED.YES } })
        ToastService.create(`Pinned ${card.name}`, 'Pinned Cards', 'success')
      })
      .catch(err => ToastService.create(err.error, 'Pinned Cards', 'error'))
  }

  return (
    <div>
      <div className={styles.cardContainer}>
        <BasicCard
          card={{ ...collectionCard.card, modifier: collectionCard.modifier }}
          className={styles.basicCard}
          imageClassName={styles.cardImage}
          onClick={actions.cardClick}
        />

        {localCardData.isMultiSelected && <MultiSelected />}

        {/* Bottom of the div so a z-index is unncessesary */}
        <CornerQuantity quantity={localCardData.quantity} loading={localCardData.loading} />

        {collectionCard.card.pinnedStatus !== OWNED.NO && (
          <div className={styles.pinnedStatus}>
            <button
              onClick={() =>
                setActiveState({ pinnedCardsOverlay: true, pinnedCardsFilteredCards: [collectionCard.card.name] })
              }
              className={`${collectionCard.card.pinnedStatus === OWNED.YES ? styles.exactPinned : styles.printingPinned}`}>
              <Icon name="pin" />
            </button>
          </div>
        )}

        <div className={styles.quantityControls}>
          <button onClick={actions.incrementCard}>
            <Icon name="plus" />
          </button>

          <button onClick={actions.decrementCard}>
            <Icon name="minus" />
          </button>
        </div>
      </div>

      <div className={styles.controls}>
        <div className={styles.prices}>
          <a
            className={styles.price}
            style={{ color: price.background }}
            title={priceName}
            href={price.url}
            tabIndex={priceSource === PRICE_SOURCE.NONE ? -1 : undefined}
            rel="noopener noreferrer"
            target="_blank">
            <Icon className={PRICE_ICON_NAMES[priceSource]} />
            {priceSource === PRICE_SOURCE.NONE ? null : price.price}
          </a>

          <a
            className={`
              ${styles.priceTwo}
              ${imageSize === 'small' ? styles.priceTwoSmall : ''}
              ${imageSize === 'normal' ? styles.priceTwoNormal : ''}
            `}
            style={{ color: price2.background }}
            title={priceName2}
            href={price2.url}
            tabIndex={priceSource2 === PRICE_SOURCE.NONE ? -1 : undefined}
            rel="noopener noreferrer"
            target="_blank">
            <Icon className={PRICE_ICON_NAMES[priceSource2]} />
            {priceSource2 === PRICE_SOURCE.NONE ? null : price2.price}
          </a>
        </div>
        {!localCardData.anyMultiSelected && (
          <ArchidektDropdown
            triggerIcon="ellipsis horizontal"
            menuClassName={styles.options}
            options={[
              { label: 'Card options', icon: 'cog', keybind: 'Click', onClick: () => actions.openCardModal('details') },
              { type: 'spacer' },
              {
                label: 'Pin card',
                icon: 'pin',
                onClick: () => handlePinCard(),
              },
              { type: 'spacer' },
              { label: 'View decks', onClick: () => actions?.openCardModal('decks'), icon: 'arrow right' },
              {
                label: 'View other records',
                onClick: () => actions?.openCardModal('relatedCards'),
                icon: 'arrow right',
              },

              {
                label: 'Delete card',
                icon: 'trash',
                keybind: 'R',
                onClick: actions.deleteCard,
                hidden: !ownsCollection,
              },
              { type: 'spacer' },
              {
                label: 'Multi-select',
                icon: 'hand pointer outline',
                keybind: 'Ctrl + Click',
                onClick: () => actions.multiSelect(),
              },
              { label: 'Copy card name', icon: 'copy', onClick: actions.copyCardName },
            ]}
            direction="up"
          />
        )}
      </div>
    </div>
  )
}

export default ImageCollectionCard
